import { InvoiceStatus } from '@rematter/frontend-lib-api-client/grpc/finance/v1/domain_pb';
import { InvoiceCreatedEvent } from '@rematter/frontend-lib-api-client/grpc/finance/v1/invoice_events_pb';
import {
  BadgeField,
  Col,
  FieldDisplay,
  humanReadableDateTime,
  ThemeColor,
} from '@rematter/ui-web';

import {
  invoiceStatusLabels,
  invoiceTypeLabels,
  paymentTermLabels,
} from '../enumLabels';

import { protoTimestampToZonedDateTimeString } from '@/audit-log/_components/_shared';
import { useTranslations } from '@/finance/i18n/useTranslations';
import { getLocale } from '~/shared/utils/locale';

const getStatusColor = (status: InvoiceStatus): ThemeColor => {
  switch (status) {
    case InvoiceStatus.PROFORMA:
      return 'neutral';
    case InvoiceStatus.PENDING:
      return 'warning';
    case InvoiceStatus.PAID:
      return 'success';
    case InvoiceStatus.VOIDED:
      return 'failure';
    default:
      return 'neutral';
  }
};

type InvoiceCreatedDetailProps = {
  event: InvoiceCreatedEvent;
};

export const InvoiceCreatedDetail = ({ event }: InvoiceCreatedDetailProps) => {
  const t = useTranslations();
  const invoice = event.invoice;

  if (!invoice) {
    return (
      <Col className="@InvoiceCreatedDetail items-center justify-center p-4 text-neutral/40">
        <span>{t.logs.noDataAvailable}</span>
      </Col>
    );
  }

  const documentDateStr = protoTimestampToZonedDateTimeString(
    invoice.documentDate,
  );
  const dueDateStr = protoTimestampToZonedDateTimeString(invoice.dueDate);

  const formatAmount = () => {
    if (!invoice.amount) return 'N/A';
    const value = Number(invoice.amount.amount) / 100;
    return `${invoice.amount.currency} ${value.toFixed(2)}`;
  };

  return (
    <Col className="@InvoiceCreatedDetail gap-3" gap3>
      <Col className="rounded-md border border-neutral/10 bg-white p-3" gap2>
        <span className="text-xs font-medium text-neutral/60">
          {t.logs.invoiceDetails}
        </span>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          <FieldDisplay
            label="Document Number"
            value={invoice.documentNumber}
          />
          <BadgeField
            label="Status"
            value={invoiceStatusLabels[invoice.status]}
            color={getStatusColor(invoice.status)}
          />
          <FieldDisplay label="Type" value={invoiceTypeLabels[invoice.type]} />
          <FieldDisplay label="Amount" value={formatAmount()} />
          {documentDateStr && (
            <FieldDisplay
              label="Document Date"
              value={humanReadableDateTime(documentDateStr, {
                locale: getLocale(),
              })}
            />
          )}
          {dueDateStr && (
            <FieldDisplay
              label="Due Date"
              value={humanReadableDateTime(dueDateStr, { locale: getLocale() })}
            />
          )}
          <FieldDisplay
            label="Payment Term"
            value={paymentTermLabels[invoice.paymentTerm]}
          />
          <FieldDisplay label="Facility ID" value={invoice.facilityId?.value} />
          <FieldDisplay label="Account ID" value={invoice.accountId?.value} />
          {invoice.notes && (
            <FieldDisplay
              label="Notes"
              value={invoice.notes}
              className="col-span-2"
            />
          )}
        </div>
      </Col>
    </Col>
  );
};
